import * as Schema from "effect/Schema";
import { IsoDateTime, NonNegativeInt, PositiveInt, TrimmedNonEmptyString } from "./baseSchemas.ts";
import {
  ChangeRequestState,
  PullRequestActor,
  PullRequestChecksState,
  PullRequestMergeability,
  PullRequestReviewDecision,
} from "./sourceControl.ts";

/**
 * Who put a pull request in a thread's collection. `stack-dismissed` is a tombstone for a
 * known native-stack member the user removed: it stays in the collection so stack discovery
 * cannot re-add it, and clients never show it.
 */
export const ThreadPullRequestLinkSource = Schema.Literals([
  "manual",
  "created",
  "agent",
  "stack",
  "stack-dismissed",
]);
export type ThreadPullRequestLinkSource = typeof ThreadPullRequestLinkSource.Type;

/**
 * Membership identity. The owning Environment qualifies it; equal host/repository/number
 * pairs in two Environments are two different memberships and must never be collapsed.
 */
export const ThreadPullRequestKey = Schema.Struct({
  host: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
});
export type ThreadPullRequestKey = typeof ThreadPullRequestKey.Type;

/**
 * What the Environment last read from the host. Null until the first successful read. An
 * observation is evidence of past state only: it never proves current permission, and it
 * never makes a mutation eligible. `observedAt` is when the read succeeded, so clients can
 * say how old it is instead of implying it was just refreshed.
 */
export const ThreadPullRequestObservation = Schema.Struct({
  state: ChangeRequestState,
  title: TrimmedNonEmptyString,
  headBranch: TrimmedNonEmptyString,
  baseBranch: TrimmedNonEmptyString,
  isDraft: Schema.Boolean,
  updatedAt: Schema.NullOr(IsoDateTime),
  observedAt: IsoDateTime,
  closedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  mergedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  author: Schema.optional(Schema.NullOr(PullRequestActor)),
  additions: Schema.optional(NonNegativeInt),
  deletions: Schema.optional(NonNegativeInt),
  changedFiles: Schema.optional(NonNegativeInt),
  reviewDecision: Schema.optional(Schema.NullOr(PullRequestReviewDecision)),
  checksState: Schema.optional(Schema.NullOr(PullRequestChecksState)),
  mergeability: Schema.optional(PullRequestMergeability),
});
export type ThreadPullRequestObservation = typeof ThreadPullRequestObservation.Type;

export const ThreadPullRequestStackLayer = Schema.Struct({
  number: PositiveInt,
  headBranch: TrimmedNonEmptyString,
  state: ChangeRequestState,
});
export type ThreadPullRequestStackLayer = typeof ThreadPullRequestStackLayer.Type;

/** Native stack metadata read from the host. Layers run bottom to top. */
export const ThreadPullRequestStack = Schema.Struct({
  kind: Schema.Literal("native"),
  id: TrimmedNonEmptyString,
  number: PositiveInt,
  url: TrimmedNonEmptyString,
  base: TrimmedNonEmptyString,
  layers: Schema.Array(ThreadPullRequestStackLayer),
});
export type ThreadPullRequestStack = typeof ThreadPullRequestStack.Type;

/**
 * One membership in a thread's pull request collection. `generation` increments every time
 * the membership is created anew, so a read that started before an unlink cannot apply its
 * result to the membership a later relink created.
 */
export const ThreadPullRequestLink = Schema.Struct({
  ...ThreadPullRequestKey.fields,
  url: TrimmedNonEmptyString,
  source: ThreadPullRequestLinkSource,
  linkedAt: IsoDateTime,
  generation: PositiveInt,
  observation: Schema.NullOr(ThreadPullRequestObservation),
  stack: Schema.NullOr(ThreadPullRequestStack),
  /** Null until the first read attempt resolves; false while a link has never been read. */
  observationUnavailable: Schema.optional(Schema.Boolean),
});
export type ThreadPullRequestLink = typeof ThreadPullRequestLink.Type;

/**
 * Thread fields carrying the collection. Both are optional so a payload written before the
 * collection existed still decodes, and so a client too old to know about them may leave them
 * out: a thread with no collection is empty at revision zero. Read them through the AFAR
 * Domain's `threadPullRequestCollectionOf`, which applies that default in one place.
 */
export const ThreadPullRequestCollectionFields = {
  pullRequests: Schema.optional(Schema.Array(ThreadPullRequestLink)),
  pullRequestsRevision: Schema.optional(NonNegativeInt),
} as const;

/**
 * Which threads hold a pull request in their collection. The Environment answers from its own
 * projection, so a thread in another Environment is never counted here however identical its
 * repository and number look.
 */
export const PullRequestLinkedThread = Schema.Struct({
  id: TrimmedNonEmptyString,
  projectId: TrimmedNonEmptyString,
  title: Schema.String,
  archivedAt: Schema.NullOr(IsoDateTime),
});
export type PullRequestLinkedThread = typeof PullRequestLinkedThread.Type;

export const PullRequestLinkedThreadsInput = ThreadPullRequestKey;
export type PullRequestLinkedThreadsInput = typeof PullRequestLinkedThreadsInput.Type;

export const PullRequestLinkedThreadsResult = Schema.Struct({
  threads: Schema.Array(PullRequestLinkedThread),
});
export type PullRequestLinkedThreadsResult = typeof PullRequestLinkedThreadsResult.Type;

/**
 * The Environment could not read its own projection. Reported rather than answered as an empty
 * collection, so a surface says it does not know instead of saying nothing is linked.
 */
export class PullRequestLinkedThreadsError extends Schema.TaggedErrorClass<PullRequestLinkedThreadsError>()(
  "PullRequestLinkedThreadsError",
  {
    detail: Schema.String,
    cause: Schema.optional(Schema.Defect()),
  },
) {}
